import { Component } from "react";
import { connect } from "react-redux";
import { Card, CardRowSimple, InfoToggletip } from "widgets";

import { ProjectColoredLink } from "@/modules/projects/components/ProjectColoredWidgets.js";
import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.tsx";

import type { IConfiguredAttribute, IConfiguredPlaybook } from "../../../app-utils/playbook.js";
import {
  createLockedSamplesWarning,
  getAttributeValueForDisplay,
  getSampleRowsForReviewScreen,
  getUniqueAttributeId,
} from "../../../app-utils/sample.js";
import type { Dictionary } from "../../../app-utils/type.js";
import type { Attribute, Group, ParentType, ProjectFile } from "../../../graphql.js";
import type { IEditable } from "../../../redux/modules/data-editing.js";
import {
  getOrderedListOfTrackerAndWorkflowAttributes,
  type IPlaybookExecutionState,
} from "../../../redux/modules/playbook-execution.js";
import type {
  ExperimentLevelAttributeValueType,
  IAttributeLookup,
  ISampleEditingState,
} from "../../../redux/modules/sample-editing.js";
import type { IAppState } from "../../../redux/reducer.js";
import { PLAYBOOK_TYPE } from "../../../settings/settings.js";
import InMemoryTableWithSelectionModel from "../../core/grid/InMemoryTableWithSelectionModel.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

export interface IExperimentValue {
  title: string;
  displayValue: string | number;
  tooltip?: string;
}

interface ICombinedRegistrationNgsReviewScreenOwnProps extends IPlaybookWizardStepProvidedProps {
  onSubmit?: () => Promise<Group | void>;
}

interface ICombinedRegistrationNgsReviewScreenAppStateProps {
  sampleEditingState: ISampleEditingState;
  playbookExecution: IPlaybookExecutionState;
  playbookConfig: IConfiguredPlaybook;
}

interface ICombinedRegistrationNgsReviewScreenDispatchProps {}

export interface ICombinedRegistrationNgsReviewScreenProps
  extends ICombinedRegistrationNgsReviewScreenAppStateProps,
    ICombinedRegistrationNgsReviewScreenDispatchProps,
    ICombinedRegistrationNgsReviewScreenOwnProps,
    IPlaybookWizardStepInjectedProps {}

function mapStateToProps(state: IAppState): ICombinedRegistrationNgsReviewScreenAppStateProps {
  return {
    sampleEditingState: state.sampleEditingState,
    playbookExecution: state.playbookExecution,
    playbookConfig: state.playbookExecution.playbook,
  };
}

function mapDispatchToProps(_dispatch): ICombinedRegistrationNgsReviewScreenDispatchProps {
  return {};
}

interface ICombinedRegistrationNgsReviewScreenState {
  complianceChecked?: boolean;
  isSubmitting?: boolean;
  errorMessage?: string;
}

class CombinedRegistrationNgsReviewScreenImpl extends Component<
  ICombinedRegistrationNgsReviewScreenProps,
  ICombinedRegistrationNgsReviewScreenState
> {
  constructor(props) {
    super(props);

    this.state = {
      complianceChecked: false,
    };
  }

  createRowData = () => {
    const data: IEditable[] = this.props.sampleEditingState.editingGrid;
    const attributeLookup: IAttributeLookup = this.props.sampleEditingState.attributeLookup;
    return getSampleRowsForReviewScreen(data, attributeLookup);
  };

  getColumnDefs = (): any[] => {
    const orderedListOfAttributes: Attribute[] = getOrderedListOfTrackerAndWorkflowAttributes(
      this.props.playbookConfig.config.sampleInput,
      this.props.sampleEditingState.sampleAttributes,
    );

    return orderedListOfAttributes.map((attribute: Attribute) => ({
      ...attribute.tableColumn,
      groupName: attribute.groupName,
      fixed: false, // so that the sample name is not back to first position, we respect the attributes ordering configuration
    }));
  };

  getExperimentValues = (): IExperimentValue[] => {
    const experimentValues: Dictionary<ExperimentLevelAttributeValueType> =
      this.props.sampleEditingState.experimentValues;

    const orderedListOfExperimentAttributes: IConfiguredAttribute[] = getOrderedListOfTrackerAndWorkflowAttributes(
      this.props.playbookConfig.config.experimentInput,
      this.props.sampleEditingState.experimentAttributes,
    );

    const parentValues: IExperimentValue[] = orderedListOfExperimentAttributes
      .map((attribute: IConfiguredAttribute) => {
        const attributeValue = experimentValues[getUniqueAttributeId(attribute)];
        return getAttributeValueForDisplay(attribute, attributeValue);
      })
      .filter((v) => !!v);

    return parentValues;
  };

  getWorkflowValues = (): IExperimentValue[] => {
    const workflowValues: Dictionary<ExperimentLevelAttributeValueType> = this.props.playbookExecution.workflowValues;

    const orderedListOfExperimentAttributes: IConfiguredAttribute[] = getOrderedListOfTrackerAndWorkflowAttributes(
      this.props.playbookConfig.config.experimentInput,
      this.props.playbookExecution.workflowAttributes,
    );

    const experimentValues: IExperimentValue[] = orderedListOfExperimentAttributes
      .map((attribute: IConfiguredAttribute) => {
        const attributeValue: any = workflowValues[getUniqueAttributeId(attribute)];
        return getAttributeValueForDisplay(attribute, attributeValue);
      })
      .filter((v) => !!v);

    if (this.props.playbookConfig.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS) {
      const ngsRawData: IExperimentValue = {
        title: "Files",
        displayValue: this.props.playbookExecution.ngsRawDataFiles
          .flat()
          .map((file: ProjectFile) => file.fileName)
          .join(", "),
        tooltip: this.props.playbookExecution.ngsRawDataFiles
          .flat()
          .map((file: ProjectFile) => file.humanReadablePath)
          .join(", "),
      };
      experimentValues.unshift(ngsRawData);
    }

    return experimentValues;
  };

  render() {
    const playbookExecutionState = this.props.playbookExecution;
    const parentType: ParentType = this.props.sampleEditingState.parentType;
    const experimentValues: any = this.getExperimentValues();
    const workflowValues: any = this.getWorkflowValues();

    const lockedSamples: IEditable[] = this.props.sampleEditingState.editingGrid.filter((r) => r.locked);
    const lockedSamplesWarning: string | null = createLockedSamplesWarning(lockedSamples?.length);

    const { eventName, eventDescription, project } = playbookExecutionState;
    return (
      <PlaybookWizardStep
        {...this.props}
        wizardFooterData={{
          error: this.state.errorMessage,
          warning: null,
          nextButtonProps: {
            label: this.props.onSubmit ? "Submit" : "Next",
            onPress: this.handleNextButton,
            isLoading: this.state.isSubmitting,
          },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", overflow: "auto" }}>
          <div style={{ marginBottom: "20px" }}>
            <Card key="contig-info-card" title={`${parentType?.name || "Submission"} Information`}>
              <CardRowSimple
                label={"Project"}
                value={<ProjectColoredLink project={project} noLink />}
                key={`parent-row-project`}
              />

              {eventName != null && <CardRowSimple label={"Event name"} value={eventName} key={`parent-row-name`} />}
              {eventDescription != null && (
                <CardRowSimple label={"Event description"} value={eventDescription} key={`parent-row-description`} />
              )}
              {this.props.sampleEditingState.sampleEditingTableQuerySpec == null &&
                experimentValues.map((p: IExperimentValue) => (
                  <CardRowSimple label={p.title} key={`parent-row-${p.title}`}>
                    <span>
                      {p.displayValue}
                      {p.tooltip && <InfoToggletip content={p.tooltip} />}
                    </span>
                  </CardRowSimple>
                ))}
              {workflowValues.map((p: IExperimentValue) => (
                <CardRowSimple label={p.title} key={`workflow-row-${p.title}`}>
                  <span>
                    {p.displayValue} {p.tooltip && <InfoToggletip content={p.tooltip} />}
                  </span>
                </CardRowSimple>
              ))}
            </Card>
          </div>

          {lockedSamplesWarning && (
            <div style={{ marginBottom: 20 }}>
              <FormWarning>{lockedSamplesWarning}</FormWarning>
            </div>
          )}
          {this.props.playbookConfig.type.id !== PLAYBOOK_TYPE.NGS_ANALYSIS && (
            <InMemoryTableWithSelectionModel
              columns={this.getColumnDefs()}
              rowGridData={this.createRowData()}
              uniqueGridIdentifier={"sample-registration-review"}
              useSelectionModel={false}
              editingTheme
              gridProps={{ domLayout: "autoHeight" }}
              showLockedColumn={true}
            />
          )}
        </div>
      </PlaybookWizardStep>
    );
  }

  handleNextButton = () => {
    this.setState({ errorMessage: null, isSubmitting: true });
    if (this.props.onSubmit) {
      return this.props.onSubmit().then(
        (_fulfilled) => {
          this.setState({ isSubmitting: false });
          if (this.props.proceedWithNextStep) {
            this.props.proceedWithNextStep();
          }
        },
        (error: Error) => {
          this.setState({ errorMessage: error.message, isSubmitting: false });
        },
      );
    } else {
      return this.props.proceedWithNextStep && this.props.proceedWithNextStep();
    }
  };
}

const CombinedRegistrationNgsReviewScreen = connect<
  ICombinedRegistrationNgsReviewScreenAppStateProps,
  ICombinedRegistrationNgsReviewScreenDispatchProps,
  ICombinedRegistrationNgsReviewScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(CombinedRegistrationNgsReviewScreenImpl as any);

export default CombinedRegistrationNgsReviewScreen;
export { CombinedRegistrationNgsReviewScreen };
